import React, { useState, useEffect } from 'react';
import {
	Heart,
	Edit3,
	Check,
	X,
	Gamepad2,
	Plane,
	Users,
	HandHeart,
	StickyNote,
	BookOpen,
} from 'lucide-react';
import Card from '../../../components/ui/card';
import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';

const PersonalDetailsCard = ({ client, clientDataHook }) => {
	const { updateCard, isCardEditMode, toggleCardEditMode, cancelCardEdit, isOnboarding, refreshData, isLoading } = clientDataHook;
	const cardName = 'personalDetails';
	const isEditing = isOnboarding || isCardEditMode(cardName);
	
	// Refresh data when component mounts (for onboarding)
	useEffect(() => {
		if (isOnboarding && refreshData) {
			console.log('🟦 PersonalDetailsCard - Component mounted, refreshing data');
			refreshData();
		}
	}, [isOnboarding]);

	// Form state for onboarding mode
	const [formValues, setFormValues] = useState({
		hobbies: client?.hobbies || '',
		travelDestinations: client?.travelDestinations || '',
		personalValues: client?.personalValues || '',
		personalStory: client?.personalStory || '',
		volunteerWork: client?.volunteerWork || '',
		otherDetails: client?.otherDetails || ''
	});

	// Update form values when client data changes
	useEffect(() => {
		setFormValues({
			hobbies: client?.hobbies || '',
			travelDestinations: client?.travelDestinations || '',
			personalValues: client?.personalValues || '',
			personalStory: client?.personalStory || '',
			volunteerWork: client?.volunteerWork || '',
			otherDetails: client?.otherDetails || ''
		});
	}, [client]);

	const fields = [
		{
			key: 'hobbies',
			label: 'Favorite hobbies',
			type: 'text',
			value: client?.hobbies || '',
			icon: Gamepad2,
			placeholder: 'Reading, hiking, photography, etc.',
		},
		{
			key: 'travelDestinations',
			label: 'Favorite place to travel and why',
			type: 'textarea',
			value: client?.travelDestinations || '',
			icon: Plane,
			placeholder: 'Tell us about your favorite destination and what makes it special...',
		},
		{
			key: 'personalValues',
			label: 'Personal values that drive your business',
			type: 'textarea',
			value: client?.personalValues || '',
			icon: Heart,
			placeholder: 'What values guide your approach to real estate?',
		},
		{
			key: 'personalStory',
			label: 'Personal story and background',
			type: 'textarea',
			value: client?.personalStory || '',
			icon: BookOpen,
			placeholder: 'Share your personal and professional journey, what led you to real estate...',
		},
		{
			key: 'volunteerWork',
			label: 'Volunteer work or community involvement',
			type: 'textarea',
			value: client?.volunteerWork || '',
			icon: HandHeart,
			placeholder: 'Organizations you support, community activities you participate in...',
		},
		{
			key: 'otherDetails',
			label: 'Additional personal details',
			type: 'textarea',
			value: client?.otherDetails || '',
			icon: StickyNote,
			placeholder: 'Any other personal information you would like to share...',
		},
	];

	// Handle save for profile mode (card-wide editing)
	const handleSave = async () => {
		const success = await updateCard(formValues);
		if (success) {
			toggleCardEditMode(cardName); // Exit edit mode
		}
	};

	// Handle cancel for profile mode (card-wide editing)
	const handleCancel = () => {
		// Reset form values to original client data
		setFormValues({
			hobbies: client?.hobbies || '',
			travelDestinations: client?.travelDestinations || '',
			personalValues: client?.personalValues || '',
			personalStory: client?.personalStory || '',
			volunteerWork: client?.volunteerWork || '',
			otherDetails: client?.otherDetails || ''
		});
		cancelCardEdit(cardName);
	};
	
	// Handle form field changes for onboarding
	const handleFormFieldChange = (fieldKey, value) => {
		setFormValues(prev => ({
			...prev,
			[fieldKey]: value
		}));
	};
	
	// Handle save for onboarding mode
	const handleOnboardingSave = async () => {
		const success = await updateCard(formValues);
		return success;
	};

	return (
		<Card>
			{!isOnboarding && (
				<Card.Header className='flex flex-row items-center justify-center space-y-0 pb-4'>
					<div className='space-y-1 text-center'>
						<Card.Title className='flex items-center gap-2 text-lg font-montserrat-medium text-gray-900 justify-center'>
							<Heart className='h-5 w-5' />
							Personal Details
						</Card.Title>
						<Card.Description className='text-sm text-gray-600'>
							Share your personal interests, values, and community involvement.
						</Card.Description>
					</div>
					{!isEditing && (
						<Button
							size='sm'
							variant='outline'
							onClick={() => toggleCardEditMode(cardName)}
							className='border-gray-300 text-gray-600 hover:bg-gray-50 ml-4'>
							<Edit3 className='h-4 w-4 mr-2' />
							Edit
						</Button>
					)}
				</Card.Header>
			)}
			<Card.Content className='space-y-4'>
				{fields.map((field) => (
					<div key={field.key} className='space-y-2'>
						<label className='text-sm font-semibold text-gray-800'>
							{field.label}
						</label>
						
						{isEditing ? (
							<div className='space-y-2'>
								{field.type === 'textarea' ? (
									<Textarea
										value={formValues[field.key] || ''}
										onChange={(e) => handleFormFieldChange(field.key, e.target.value)}
										placeholder={field.placeholder}
										className='w-full min-h-[80px]'
									/>
								) : (
									<Input
										type={field.type}
										value={formValues[field.key] || ''}
										onChange={(e) => handleFormFieldChange(field.key, e.target.value)}
										placeholder={field.placeholder}
										className='w-full'
									/>
								)}
							</div>
						) : (
							<div className='flex items-start gap-2'>
								<field.icon className='h-4 w-4 text-gray-500 mt-0.5' />
								<div className='flex-1'>
									<span className='text-sm font-montserrat-regular text-gray-900 whitespace-pre-wrap'>
										{field.value || (
											<span className='text-gray-400 italic'>
												{field.placeholder}
											</span>
										)}
									</span>
								</div>
							</div>
						)}
					</div>
				))}
				
				{/* Save/Cancel buttons when editing in profile mode */}
				{isEditing && !isOnboarding && (
					<div className='flex justify-end gap-3 pt-4 border-t border-gray-200'>
						<Button
							variant='outline'
							onClick={handleCancel}
							className='border-gray-300 text-gray-600 hover:bg-gray-50'>
							<X className='h-4 w-4 mr-2' />
							Cancel
						</Button>
						<Button
							onClick={handleSave}
							className='bg-gray-900 hover:bg-gray-800 text-white'>
							<Check className='h-4 w-4 mr-2' />
							Save Changes
						</Button>
					</div>
				)}
				
				{/* Save button for onboarding */}
				{isOnboarding && (
					<div className='pt-6 mt-6 border-t border-gray-200'>
						<Button
							onClick={handleOnboardingSave}
							className='w-full bg-gray-900 hover:bg-gray-800 text-white'>
							<Check className='h-4 w-4 mr-2' />
							Save & Continue
						</Button>
					</div>
				)}
			</Card.Content>
		</Card>
	);
};

export default PersonalDetailsCard;